/**
 * The GPS pipeline.
 *
 * `watchPosition` with high accuracy on, every fix graded, and fixes worse than a hundred
 * metres excluded from the arrow while the status line still explains what is happening
 * (`ProductOverview.md` §4.6). The provider never hides a bad fix — it labels it.
 */

import { CONFIDENCE, POSITION, confidenceFor, type Confidence, type LatLng } from '@beeline/shared';

export type LocationPermission = 'granted' | 'denied' | 'unavailable' | 'timeout' | 'unknown';

export interface Fix extends LatLng {
  /** Reported accuracy in metres. */
  accuracy: number;
  /** Local clock when the fix arrived. */
  at: number;
  confidence: Confidence;
  /** True when this fix is good enough to compute a bearing from. */
  usable: boolean;
  /**
   * Metres above the ellipsoid, where the device reports it.
   *
   * Only ever surfaced when the vertical gap between two people is big enough to be the actual
   * problem — a ski slope, a stadium tier, a terraced beach. GPS altitude is far worse than its
   * horizontal accuracy, so at street level this is noise and nothing shows it.
   */
  altitude: number | null;
  /** Ground speed in metres per second, where the device reports it. */
  speed: number | null;
  /**
   * Course over ground in degrees clockwise from true north.
   *
   * The direction the device is actually *travelling*, which is a different thing from the
   * direction it is *pointing*. Above walking pace this is far more trustworthy than a phone
   * magnetometer, and `HeadingProvider` blends the two on exactly that basis.
   */
  course: number | null;
}

export interface LocationError {
  permission: LocationPermission;
  message: string;
}

/** What the user should do about it, per platform, when location is refused. */
export const RECOVERY_HINT = {
  ios: 'Settings → Apps → Safari → Location, then reload this page.',
  android: 'Tap the lock icon in the address bar → Permissions → Location, then reload.',
  desktop: 'Click the icon at the left of the address bar and allow location, then reload.',
} as const;

export function platformHint(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return RECOVERY_HINT.ios;
  if (/Android/.test(ua)) return RECOVERY_HINT.android;
  return RECOVERY_HINT.desktop;
}

export const locationIsSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'geolocation' in navigator;

/** A field the platform may report as a number, as null, or as NaN. Only a number is a number. */
const reported = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Turn a browser position into our own shape, grading it on the way through. */
export function fixFrom(position: GeolocationPosition, at: number = Date.now()): Fix {
  const accuracy = position.coords.accuracy;
  const confidence = confidenceFor(accuracy);

  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy,
    at,
    confidence,
    usable: confidence !== 'unusable',
    altitude: reported(position.coords.altitude),
    speed: reported(position.coords.speed),
    course: reported(position.coords.heading),
  };
}

const permissionFor = (error: GeolocationPositionError): LocationPermission => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'denied';
    case error.POSITION_UNAVAILABLE:
      return 'unavailable';
    case error.TIMEOUT:
      return 'timeout';
    default:
      return 'unknown';
  }
};

const MESSAGE_FOR: Record<LocationPermission, string> = {
  denied: 'Location is blocked for this page.',
  unavailable: 'Your device cannot get a location right now.',
  timeout: 'Still looking for a signal…',
  unknown: 'Location is not working.',
  granted: '',
};

export class LocationProvider {
  private watchId: number | null = null;
  private lastFix: Fix | null = null;

  get latest(): Fix | null {
    return this.lastFix;
  }

  /**
   * Ask for one fix, which is what triggers the browser's permission prompt.
   * Called from a tap so the prompt is attached to something the user just did.
   */
  async requestOnce(): Promise<{ permission: LocationPermission; fix: Fix | null }> {
    if (!locationIsSupported()) return { permission: 'unavailable', fix: null };

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const fix = fixFrom(position);
          this.lastFix = fix;
          resolve({ permission: 'granted', fix });
        },
        (error) => resolve({ permission: permissionFor(error), fix: null }),
        {
          enableHighAccuracy: true,
          // Generous: the first fix outdoors can genuinely take this long from cold.
          timeout: 20_000,
          maximumAge: 0,
        },
      );
    });
  }

  /** Start watching. Every fix is reported, good or bad; the caller decides what to draw. */
  start(onFix: (fix: Fix) => void, onError?: (error: LocationError) => void): void {
    if (this.watchId !== null || !locationIsSupported()) return;

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        // A fix the browser has been sitting on for a minute is worse than none.
        const age = Date.now() - position.timestamp;
        if (Number.isFinite(age) && age > POSITION.maxFixAgeMs) return;

        const fix = fixFrom(position);
        this.lastFix = fix;
        onFix(fix);
      },
      (error) => {
        onError?.({ permission: permissionFor(error), message: MESSAGE_FOR[permissionFor(error)] });
      },
      {
        enableHighAccuracy: true,
        timeout: 30_000,
        // Never re-serve a cached fix: the whole product is about where you are now.
        maximumAge: 0,
      },
    );
  }

  stop(): void {
    if (this.watchId === null) return;
    navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }

  /** Feed a fix in from somewhere else — the keyboard fallback and the simulator use this. */
  inject(fix: Fix): void {
    this.lastFix = fix;
  }
}

/** Re-exported so views do not have to reach into the config module for one number. */
export const REJECT_ACCURACY_METRES = CONFIDENCE.rejectMetres;
